import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { message } from 'antd';
import { EditorContext, CustomAvatar } from './editor-context';
import { CardOption } from '../components/shared/card-selector';
import { useVideoForm } from '../hooks/use-video-form';
import { useCreateAvatarProject } from '../hooks/use-create-avatar-project';
import { useUpdateAvatarProject } from '../hooks/use-update-avatar-project';
import { useGetAvatarProjectDetails } from '../hooks/use-get-avatar-project-details';
import { useGetAvatarProjectFormData } from '../hooks/use-get-avatar-project-form-data';
import { useListAvatarAssets } from '../hooks/use-list-avatar-assets';
import { usePolling } from '../../../hooks/use-polling';
import { parseFormData } from '../utils/parse-form-data';
import { DIMENSION_MAP, dimensionToResolutionId } from '../utils/build-payload';
import { buildUpdatePayload } from '../utils/build-update-payload';
import { PROMPT_PRESETS } from '../constants/prompt-presets';
import { ProjectScene } from '../../../types/avatar-video';

export const EditorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // ── Form state ──
  const {
    form,
    setField,
    resetScene,
    resetForm,
    loadForm,
    getPayload,
    addScene,
    removeScene,
    duplicateScene,
    updateScene,
    setSceneOnlyVoice,
    isValid
  } = useVideoForm();

  // ── API hooks ──
  const { createAvatarProject } = useCreateAvatarProject();
  const { updateAvatarProject } = useUpdateAvatarProject();
  const { getProjectDetails } = useGetAvatarProjectDetails();
  const { getProjectFormData } = useGetAvatarProjectFormData();
  const { listAvatarAssets } = useListAvatarAssets();

  // ── Local state ──
  const [activeIndex, setActiveIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectStatus, setProjectStatus] = useState<'idle' | 'processing' | 'completed' | 'partial' | 'failed'>(
    'idle'
  );
  const [projectScenes, setProjectScenes] = useState<ProjectScene[]>([]);
  const [backgrounds, setBackgrounds] = useState<CardOption[]>([]);
  const [avatars, setAvatars] = useState<CustomAvatar[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [scenePanelCollapsed, setScenePanelCollapsed] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('vertical');
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [projectTotalScenes, setProjectTotalScenes] = useState(0);
  const [projectCompletedScenes, setProjectCompletedScenes] = useState(0);
  const [selectedPlacaId, setSelectedPlacaId] = useState<string | null>(null);
  const [selectedMusicTrackId, setSelectedMusicTrackId] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [enableSubtitles, setEnableSubtitles] = useState(true);

  const handleSubmit = useCallback(async () => {
    const dimension = DIMENSION_MAP[selectedResolution] || DIMENSION_MAP.fullhd;
    const body = {
      payload: getPayload(dimension, selectedResolution),
      placaId: selectedPlacaId,
      musicTrackId: selectedMusicTrackId,
      subtitles: enableSubtitles
    };
    setIsProcessing(true);
    setProjectScenes([]);
    const res = await createAvatarProject(body);
    if (res) {
      setProjectId(res.projectId);
      setProjectStatus('processing');
      setProgressModalOpen(true);
      localStorage.setItem('activeProjectId', res.projectId);
    } else {
      message.error('Error al crear el proyecto. Por favor intenta de nuevo.');
      console.error('[Submit] error al crear proyecto');
      setIsProcessing(false);
      setProjectStatus('failed');
    }
  }, [getPayload, createAvatarProject, selectedResolution, selectedPlacaId, selectedMusicTrackId, enableSubtitles]);

  const handleUpdate = useCallback(async () => {
    if (!editingProjectId) {
      console.warn('[Update] llamado sin editingProjectId — no debería pasar');
      return;
    }
    let body;
    try {
      body = buildUpdatePayload(form, PROMPT_PRESETS, selectedPlacaId, selectedMusicTrackId, selectedResolution, enableSubtitles);
    } catch (err) {
      console.error('[Update] error armando payload:', err);
      message.error('Hay escenas con vestuario/ambiente/plano inválido. Revisá el form.');
      return;
    }
    setIsProcessing(true);
    setProjectScenes([]);
    const res = await updateAvatarProject(editingProjectId, body);
    if (res) {
      setProjectId(editingProjectId);
      setProjectStatus('processing');
      setProgressModalOpen(true);
      localStorage.setItem('activeProjectId', editingProjectId);
    } else {
      message.error('Error al actualizar el proyecto. Por favor intenta de nuevo.');
      console.error('[Update] error al actualizar proyecto');
      setIsProcessing(false);
      setProjectStatus('failed');
    }
  }, [editingProjectId, form, selectedPlacaId, selectedMusicTrackId, selectedResolution, enableSubtitles, updateAvatarProject]);

  // ── Poll project status ──
  const handleProjectStatus = useCallback(
    async (id: string) => {
      const res = await getProjectDetails(id);
      if (!res) {
        console.warn('[ProjectStatus] fallo al obtener detalles');
        return;
      }

      setProjectScenes(res.scenes);
      setProjectTotalScenes(res.totalScenes);
      setProjectCompletedScenes(res.completedScenes);

      if (res.status === 'completed' || res.status === 'partial') {
        setIsProcessing(false);
        setProjectStatus(res.status);
      } else if (res.status === 'failed') {
        setIsProcessing(false);
        setProjectStatus('failed');
      }
    },
    [getProjectDetails]
  );

  const handleRegenerate = useCallback(
    async (regenerateProjectId: string) => {
      if (isProcessing) {
        message.warning('Actualmente se está generando un proyecto. Por favor espera a que termine.');
        return;
      }
      const data = await getProjectFormData(regenerateProjectId);
      if (!data) {
        message.error('No se pudo cargar el proyecto para regenerar');
        return;
      }
      const formState = parseFormData(data);
      if (!formState.scenes.length) {
        console.warn('El proyecto seleccionado no tiene escenas válidas para regenerar');
        return;
      }
      loadForm(formState);
      setSelectedPlacaId(data.placaId);
      setSelectedMusicTrackId(data.musicTrackId);
      setEnableSubtitles(data.subtitles ?? true);
      // Restaurar `selectedResolution` desde el dimension persistido. Sin esto,
      // buildUpdatePayload usaría el default ('fullhd').
      setSelectedResolution(dimensionToResolutionId(data.dimension));
      setProjectStatus('idle');
      setActiveIndex(0);
      setEditingProjectId(regenerateProjectId);
    },
    [isProcessing, loadForm, getProjectFormData]
  );

  // ── Load assets ──
  useEffect(() => {
    const loadAssets = async () => {
      const res = await listAvatarAssets();
      if (!res) return;
      const bgs: CardOption[] = [];
      const avs: CustomAvatar[] = [];
      for (const item of res.items) {
        if (item.type === 'AVATAR_BACKGROUND') {
          bgs.push({ id: item.assetId, label: item.name, imageUrl: item.url });
        } else if (item.type === 'AVATAR_LOOK' && item.avatarId && item.avatarGroupId) {
          avs.push({ id: item.avatarId, label: item.name, imageUrl: item.url, groupId: item.avatarGroupId });
        }
      }
      setBackgrounds(bgs);
      setAvatars(avs);
    };
    loadAssets();
  }, [listAvatarAssets]);

  // ── Restore active project on mount ──
  useEffect(() => {
    const savedProjectId = localStorage.getItem('activeProjectId');
    if (!savedProjectId) return;

    const restoreProject = async () => {
      const res = await getProjectDetails(savedProjectId);
      if (!res) {
        localStorage.removeItem('activeProjectId');
        return;
      }

      if (res.status !== 'processing') {
        localStorage.removeItem('activeProjectId');
        return;
      }

      setProjectId(savedProjectId);
      setProjectScenes(res.scenes);
      setProjectTotalScenes(res.totalScenes);
      setProjectCompletedScenes(res.completedScenes);
      setProgressModalOpen(true);
      setIsProcessing(true);
      setProjectStatus('processing');
    };

    restoreProject();
  }, [getProjectDetails]);

  // ── Polling ──
  usePolling({
    onPoll: () => {
      if (projectId) handleProjectStatus(projectId);
    },
    enabled: isProcessing && projectId !== null,
    interval: 10000
  });

  // ── Keep activeIndex in bounds ──
  useEffect(() => {
    if (activeIndex >= form.scenes.length) {
      setActiveIndex(Math.max(0, form.scenes.length - 1));
    }
  }, [form.scenes.length, activeIndex]);

  // ── Scene actions (depend on activeIndex) ──
  const onDuplicate = useCallback(() => {
    duplicateScene(activeIndex);
    setActiveIndex(activeIndex + 1);
  }, [activeIndex, duplicateScene]);

  const onRemove = useCallback(() => removeScene(activeIndex), [activeIndex, removeScene]);
  const onReset = useCallback(() => resetScene(activeIndex), [activeIndex, resetScene]);

  const closeProgressModal = useCallback(() => {
    setProgressModalOpen(false);
    resetForm();
    setProjectId(null);
    setProjectScenes([]);
    setProjectStatus('idle');
    setProjectTotalScenes(0);
    setProjectCompletedScenes(0);
    setEditingProjectId(null);
    setEnableSubtitles(true);
    localStorage.removeItem('activeProjectId');
  }, [resetForm]);

  // ── Context value ──
  const value = {
    form: { title: form.title, scenes: form.scenes },
    title: form.title,
    onTitleChange: (v: string) => setField('title', v),

    scene: form.scenes[activeIndex],
    activeIndex,
    totalScenes: form.scenes.length,
    setActiveIndex,

    updateScene,
    setSceneOnlyVoice,
    addScene: () => {
      addScene();
      setActiveIndex(form.scenes.length);
    },
    onDuplicate,
    onRemove,
    onReset,

    avatars,
    onAvatarUploaded: (opt: CustomAvatar) => setAvatars(prev => [...prev, opt]),
    backgrounds,
    onBackgroundUploaded: (opt: CardOption) => setBackgrounds(prev => [...prev, opt]),

    projectId,
    projectStatus,
    projectScenes,
    projectTotalScenes,
    projectCompletedScenes,
    isProcessing,
    isValid,
    onGenerate: handleSubmit,
    onUpdate: handleUpdate,
    onRegenerate: handleRegenerate,
    loadProjectForEdit: handleRegenerate,
    editingProjectId,

    selectedResolution,
    setSelectedResolution,

    selectedPlacaId,
    setSelectedPlacaId,

    selectedMusicTrackId,
    setSelectedMusicTrackId,

    enableSubtitles,
    setEnableSubtitles,

    historyOpen,
    setHistoryOpen,
    scenePanelCollapsed,
    toggleScenePanel: () => setScenePanelCollapsed(prev => !prev),
    progressModalOpen,
    closeProgressModal
  };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};
